import { makeStyles } from 'tss-react/mui';
import MuiPopper, { PopperProps } from '@mui/material/Popper';
import Paper, { PaperProps } from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Fade, FadeProps, Grow } from '@mui/material';

const useStyles = makeStyles<
    { placement?: PopperProps['placement']; zIndex: number },
    'arrow'
>()((theme, { placement, zIndex }, classes) => ({
    popper: {
        padding: theme.spacing(
            placement?.startsWith('bottom') ? 1 : 0,
            placement?.startsWith('left') ? 1 : 0,
            placement?.startsWith('top') ? 1 : 0,
            placement?.startsWith('right') ? 1 : 0
        ),
        zIndex,
        [`&[data-popper-placement*="bottom"] .${classes.arrow}`]: {
            'top': 0,
            'left': 0,
            'marginTop': '-0.9em',
            'width': '3em',
            'height': '1em',
            '&::before': {
                borderWidth: '0 1em 1em 1em',
                borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
            },
        },
        [`&[data-popper-placement*="top"] .${classes.arrow}`]: {
            'bottom': 0,
            'left': 0,
            'marginBottom': '-0.9em',
            'width': '3em',
            'height': '1em',
            '&::before': {
                borderWidth: '1em 1em 0 1em',
                borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
            },
        },
        [`&[data-popper-placement*="right"] .${classes.arrow}`]: {
            'left': 0,
            'marginLeft': '-0.9em',
            'height': '3em',
            'width': '1em',
            '&::before': {
                borderWidth: '1em 1em 1em 0',
                borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
            },
        },
        [`&[data-popper-placement*="left"] .${classes.arrow}`]: {
            'right': 0,
            'marginRight': '-0.9em',
            'height': '3em',
            'width': '1em',
            '&::before': {
                borderWidth: '1em 0 1em 1em',
                borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
            },
        },
    },
    arrow: {
        'position': 'absolute',
        'fontSize': 7,
        'width': '3em',
        'height': '3em',
        '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            width: 0,
            height: 0,
            borderStyle: 'solid',
        },
    },
    disableZIndex: {
        zIndex: 'auto',
    },
    hideOverflow: {
        overflow: 'hidden',
    },
    doubleBorderRadius: {
        borderRadius: theme.shape.borderRadius * 2,
    },
}));

interface Props extends PopperProps {
    children: JSX.Element;
    flip?: boolean;
    handleClose?: (e: MouseEvent | TouchEvent) => void;
    PaperProps?: PaperProps;
    animation?: 'fade' | 'grow';
    TransitionProps?: Omit<FadeProps, 'children'>;
    offset?: number;
    placement?: PopperProps['placement'];
    zIndex?: number;
    disableZIndex?: boolean;
    hideOverflow?: boolean;
    doubleBorderRadius?: boolean;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export default function Popper({
    children,
    flip,
    handleClose,
    PaperProps,
    animation = 'fade',
    TransitionProps: transitionProps,
    offset,
    placement,
    zIndex = 10000,
    onClick,
    disableZIndex,
    hideOverflow,
    doubleBorderRadius,
    transition = true,
    ...props
}: Props) {
    const { classes, theme, cx } = useStyles({ placement, zIndex });
    const Transition = animation === 'grow' ? Grow : Fade;
    // https://popper.js.org/docs/v2/modifiers/ check to fix the modifier object. no priority as we dont user the arrows
    return (
        <MuiPopper
            className={cx(classes.popper, {
                [classes.disableZIndex]: disableZIndex,
            })}
            placement={placement}
            transition={transition}
            {...props}>
            {({ TransitionProps, placement }) => (
                <Transition
                    {...TransitionProps}
                    {...transitionProps}
                    timeout={{
                        enter: theme.transitions.duration.standard,
                        exit: theme.transitions.duration.standard,
                    }}
                    {...(animation === 'grow' && {
                        id: 'menu-list-grow',
                        style: {
                            transformOrigin:
                                placement === 'bottom'
                                    ? 'center top'
                                    : 'center bottom',
                        },
                    })}>
                    <Paper
                        {...PaperProps}
                        classes={{
                            ...PaperProps?.classes,
                            root: cx(PaperProps?.classes?.root, {
                                [classes.hideOverflow]: hideOverflow,
                                [classes.doubleBorderRadius]:
                                    doubleBorderRadius,
                            }),
                        }}
                        onClick={onClick ?? PaperProps?.onClick}>
                        <ClickAwayListener
                            onClickAway={e =>
                                handleClose ? handleClose(e) : null
                            }>
                            {children}
                        </ClickAwayListener>
                    </Paper>
                </Transition>
            )}
        </MuiPopper>
    );
}
